import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout/Layout';
import { usersApi, User } from '../services/api';
import { SettingsTab } from '../components/Admin/SettingsTab';

type TabType = 'users' | 'settings';

export const AdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await usersApi.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await usersApi.create({ username, password, email: email || undefined });
      setUsername('');
      setEmail('');
      setPassword('');
      setShowForm(false);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (id: number, username: string) => {
    if (!confirm(`Delete user "${username}"?`)) {
      return;
    }

    try {
      await usersApi.delete(id);
      loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete user');
    }
  };

  return (
    <Layout showSidebar={false}>
      <div>
        <h1 style={{ marginBottom: '24px' }}>Admin Panel</h1>

        {/* Tabs */}
        <div style={{ borderBottom: '2px solid var(--border)', marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setActiveTab('users')}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === 'users' ? '2px solid var(--accent)' : '2px solid transparent',
                color: activeTab === 'users' ? 'var(--accent)' : 'var(--text-secondary)',
                fontWeight: activeTab === 'users' ? 600 : 400,
                cursor: 'pointer',
                marginBottom: '-2px',
              }}
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === 'settings' ? '2px solid var(--accent)' : '2px solid transparent',
                color: activeTab === 'settings' ? 'var(--accent)' : 'var(--text-secondary)',
                fontWeight: activeTab === 'settings' ? 600 : 400,
                cursor: 'pointer',
                marginBottom: '-2px',
              }}
            >
              Settings
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'users' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2>User Management</h2>
              <button
                onClick={() => setShowForm(!showForm)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  background: 'var(--accent)',
                  color: 'white',
                  fontWeight: 500
                }}
              >
                {showForm ? 'Cancel' : 'Create User'}
              </button>
            </div>

            {showForm && (
              <div style={{ background: 'var(--bg-secondary)', padding: '24px', borderRadius: '8px', marginBottom: '24px', border: '1px solid var(--border)' }}>
                <h3 style={{ marginBottom: '20px' }}>Create New User</h3>
                <form onSubmit={handleCreateUser}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Username</label>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      minLength={3}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Email (optional)</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  {error && <div style={{ color: 'var(--error)', marginBottom: '16px' }}>{error}</div>}
                  <button
                    type="submit"
                    style={{ padding: '10px 20px', borderRadius: '6px', background: 'var(--accent)', color: 'white', fontWeight: 500 }}
                  >
                    Create User
                  </button>
                </form>
              </div>
            )}

            {loading ? (
              <p>Loading...</p>
            ) : (
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: 'var(--bg-tertiary)' }}>
                    <tr>
                      <th style={{ padding: '16px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Username</th>
                      <th style={{ padding: '16px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Email</th>
                      <th style={{ padding: '16px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Admin</th>
                      <th style={{ padding: '16px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>OAuth</th>
                      <th style={{ padding: '16px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Created</th>
                      <th style={{ padding: '16px', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>{user.username}</td>
                        <td style={{ padding: '16px', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{user.email || '-'}</td>
                        <td style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>{user.is_admin ? 'Yes' : 'No'}</td>
                        <td style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>
                          {user.oauth_provider ? (
                            <span style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '6px',
                              padding: '4px 8px', 
                              borderRadius: '4px', 
                              background: 'var(--accent-alpha)', 
                              color: 'var(--accent)',
                              fontSize: '13px'
                            }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                              </svg>
                              {user.oauth_provider}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '16px', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '16px', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>
                          <button
                            onClick={() => handleDeleteUser(user.id, user.username)}
                            style={{ padding: '6px 12px', borderRadius: '4px', background: 'var(--error)', color: 'white', fontSize: '13px' }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && <SettingsTab />}
      </div>
    </Layout>
  );
};
